import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';

// Use relative URL in production (Vercel), full URL in development
import { API } from '@/config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sync user with backend profile
  const syncUser = async (email) => {
    if (!email) return null;
    try {
      const response = await axios.post(`${API}/auth/login`, { email: email.toLowerCase() });
      const userData = response.data;
      setUser(userData);
      localStorage.setItem('dondoit_user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Error syncing user with backend:', error);
      return null;
    }
  };

  useEffect(() => {
    // Check active sessions and sets up a listener to auth events
    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await syncUser(session.user.email);
      } else {
        // Fallback to local storage for impersonation or session persistence
        const savedUser = localStorage.getItem('dondoit_user');
        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }
      }
      setLoading(false);
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        await syncUser(session.user.email);
      } else if (_event === 'SIGNED_OUT') {
        setUser(null);
        localStorage.removeItem('dondoit_user');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.toLowerCase(),
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
      return { success: true, message: 'Check your email for the magic link! 🤌📧' };
    } catch (error) {
      console.error('Magic Link error:', error);
      return { success: false, error: error.message || 'Errore durante l\'invio del Magic Link.' };
    }
  };

  const signup = async (name, email) => {
    try {
      // First register in our backend to define role/name
      const response = await axios.post(`${API}/auth/signup`, {
        name: name.trim(),
        email: email.toLowerCase()
      });

      // Then send magic link
      await login(email);

      return { success: true, user: response.data };
    } catch (error) {
      console.error('Signup error details:', error);
      const backendError = error.response?.data?.detail;

      if (!error.response || error.code === 'ERR_NETWORK') {
        return { success: false, error: 'Impossibile connettersi al Server! 🤌🔥 Controlla se il backend è attivo.' };
      }

      return {
        success: false,
        error: backendError || 'Errore durante la registrazione. Riprova! 🤌🇫🇷'
      };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    localStorage.removeItem('dondoit_user');
  };

  const impersonateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('dondoit_user', JSON.stringify(userData));
  };

  const value = {
    user,
    login,
    signup,
    logout,
    impersonateUser,
    loading,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager',
    isMember: user?.role === 'member',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
